import { LoadingButton } from "@mui/lab";
import {
    Button, Card, CardContent, CardHeader, Chip, FormControlLabel, Grid, IconButton,
    Switch, Tooltip, Typography
} from "@mui/material"
import { useCallback, useEffect, useState } from "react";
import { FormProvider, RHFSelectField, RHFTextField } from "../../../common/hook-form";
import { useForm } from "react-hook-form";
import { apiConstants, deleteGeneric, get, postGeneric, putGeneric } from "../../../common/constants";
import { useSnackbar } from "notistack";
import PageTitle from "../../../components/PageTitle";
import ProzoDataGrid from "../../../common/ProzoDataGrid";
import MUIModal from "../../../components/MuiModal";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import { designation } from "../../../common/util";
import _ from "lodash";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { invalidateVM } from "../../leadership/engine";

// One slot per role per merchant (KAM_SAM_SL_MONGO_MAPPING_DESIGN.md): the
// backend stores this on MerchantConfig.salesMapping and validates that the
// email belongs to a real proship user with access to this merchant.
const apiError = (err, fallback) =>
    err?.response?.data?.message || err?.response?.data?.error || fallback

export default function KamDetails({ merchant, id }) {
    const { enqueueSnackbar } = useSnackbar();
    const [sizePerPage, setSizePerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [modal, setModal] = useState(null); // null | {mode:'add'} | {mode:'edit', row}
    const [mapping, setMapping] = useState({ kam: [], priority: false });
    const [reload, setReload] = useState(0);
    const [priorityBusy, setPriorityBusy] = useState(false);
    const [deletingRole, setDeletingRole] = useState(null);

    useEffect(() => {
        // GET /sales/mapping/{id} — same legacy-shaped response as the old
        // system/default/kam/MERCHANT fetch, but its gate also admits
        // SALES_DASHBOARD holders for merchants on their own access list
        // (the legacy endpoint is hard-locked to Proship admins).
        get(`${apiConstants.SALES_MAPPING}/${id}`).then((res) => {
            setMapping({
                kam: res?.data?.kam || [],
                priority: !!res?.data?.priority,
            });
        }).catch((err) => console.log(err))
    }, [reload, id])

    const refresh = useCallback(() => {
        invalidateVM()
        setReload(r => r + 1)
    }, [])

    const handleDelete = (row) => {
        // eslint-disable-next-line no-restricted-globals
        if (!window.confirm(`Remove ${row.designation} (${row.name}) from this merchant? The user account itself is not deleted.`)) {
            return;
        }
        setDeletingRole(row.role)
        deleteGeneric(`${apiConstants.SALES_MAPPING}/${id}/${row.role}`).then(() => {
            enqueueSnackbar("Mapping removed", { variant: 'success' })
            refresh()
        }).catch((err) => {
            enqueueSnackbar(apiError(err, "Failed to remove mapping"), { variant: 'error' })
        }).finally(() => setDeletingRole(null))
    }

    const handlePriority = (checked) => {
        setPriorityBusy(true)
        putGeneric(`${apiConstants.SALES_MAPPING}/${id}/priority`, { priority: checked }).then(() => {
            enqueueSnackbar(checked ? "Marked as priority merchant" : "Priority flag removed", { variant: 'success' })
            refresh()
        }).catch((err) => {
            enqueueSnackbar(apiError(err, "Failed to update priority"), { variant: 'error' })
        }).finally(() => setPriorityBusy(false))
    }

    const columns = [
        {
            field: 'name', headerAlign: "left", hideSortIcons: true, headerName: 'Name', flex: 1, renderCell: (params) => {
                const { row } = params;
                return <Typography sx={{ fontSize: 'small' }}>{row?.name}</Typography>;
            },
        },
        {
            field: 'email', headerAlign: "left", hideSortIcons: true, headerName: 'Email', flex: 1, renderCell: (params) => {
                const { row } = params;
                return <Typography sx={{ fontSize: 'small' }}>{row?.email}</Typography>;
            },
        },
        {
            field: 'designation', headerAlign: "left", hideSortIcons: true, headerName: 'Designation', flex: 1, renderCell: (params) => {
                const { row } = params;
                return <Chip size="small" label={row?.designation} />;
            },
        },
        {
            field: 'actions', headerAlign: "left", hideSortIcons: true, headerName: 'Actions', flex: 0.7, renderCell: (params) => {
                const { row } = params;
                return (
                    <>
                        <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => setModal({ mode: 'edit', row })}>
                                <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove mapping">
                            <span>
                                <IconButton size="small" color="error" disabled={deletingRole === row.role}
                                    onClick={() => handleDelete(row)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </>
                );
            },
        },
    ];

    const assigned = (mapping.kam || []).map(r => r.designation)
    const available = designation.filter(d => !assigned.includes(d))

    return (
        <>
            <PageTitle>Personnel Mapping {merchant ? " / " + merchant : ""}</PageTitle>
            <Card>
                <CardHeader
                    action={
                        <Grid container spacing={1} alignItems="center">
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={mapping.priority}
                                            disabled={priorityBusy}
                                            onChange={(e) => handlePriority(e.target.checked)}
                                            color="warning"
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Priority Merchant</Typography>}
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    disabled={available.length === 0}
                                    onClick={() => setModal({ mode: 'add' })}
                                    startIcon={<AddCircleOutlineIcon />}
                                >
                                    Add Details
                                </Button>
                            </Grid>
                        </Grid>
                    }
                />
                <CardContent>
                    <MUIModal open={!!modal} handleClose={() => setModal(null)} children={
                        <PersonForm
                            id={id}
                            mode={modal?.mode}
                            row={modal?.row}
                            availableDesignations={available}
                            handleClose={() => setModal(null)}
                            onSaved={refresh}
                        />}
                        title={modal?.mode === 'edit' ? `Edit ${modal?.row?.designation}` : 'Add Details'}
                        maxWidth='lg'
                    />
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <ProzoDataGrid
                                columns={columns}
                                autoHeight={true}
                                rows={(mapping.kam && mapping.kam.length > 0)
                                    ? mapping.kam.map((row, index) => ({ ...row, id: index + 1 }))
                                    : []
                                }
                                sizePerPage={sizePerPage}
                                setSizePerPage={setSizePerPage}
                                setPage={setPage}
                                page={page}
                                pagination={false}
                                rowHeight={120}
                                rowCount={Number.MAX_VALUE}
                                hideCountNumber={false}
                                getRowId={(row) => row.id}
                            />
                        </Grid>
                    </Grid >
                </CardContent>
            </Card>
        </>
    )
}

const PersonForm = ({ id, mode, row, availableDesignations, handleClose, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const emailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isEdit = mode === 'edit'
    const options = (isEdit ? [row?.designation] : availableDesignations)
        .map((op) => ({ label: _.startCase(op), value: op }))
    const [loading, setLoading] = useState(false)

    const method = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                name: Yup.string().required("Name is required"),
                email: Yup.string()
                    .matches(emailRegExp, { message: 'Invalid email format', excludeEmptyString: true })
                    .required("Email is required"),
                designation: Yup.string().required("Please select designation")
            })
        ),
        defaultValues: {
            name: isEdit ? (row?.name || '') : '',
            email: isEdit ? (row?.email || '') : '',
            designation: isEdit ? (row?.designation || '') : '',
        },
    })

    const {
        handleSubmit,
    } = method;

    const onSubmit = (data) => {
        setLoading(true)
        const done = (msg) => {
            enqueueSnackbar(msg, { variant: 'success' })
            setLoading(false)
            handleClose()
            onSaved()
        }
        const fail = (err) => {
            enqueueSnackbar(apiError(err, "Some error occurred"), { variant: 'error' })
            setLoading(false)
        }
        if (isEdit) {
            putGeneric(`${apiConstants.SALES_MAPPING}/${id}/${row.role}`, {
                name: data.name,
                email: data.email,
            }).then(() => done("Successfully Updated")).catch(fail)
        } else {
            // single-slot add — the backend rejects a second entry for the same
            // role. POST /sales/mapping/{id} (not the legacy admin-only
            // system/default/kam, which the courier screen still uses): its gate
            // also admits SALES_DASHBOARD holders for their own merchants.
            postGeneric(`${apiConstants.SALES_MAPPING}/${id}`, {
                name: data.name,
                email: data.email,
                designation: data.designation,
            }).then(() => done("Successfully Added")).catch(fail)
        }
    };

    return (
        <Card>
            <CardContent>
                <FormProvider methods={method} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <RHFTextField label="Name *" name="name" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <RHFTextField label="Email *" name="email"
                                helperText="Must be an existing proship user with access to this merchant" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <RHFSelectField width="100%" name="designation" label={`Select Designation`}
                                options={options} disabled={isEdit} />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={2} sx={{ mt: 4 }}>
                        <LoadingButton loading={loading} type="submit" variant="contained" color="primary" >Save </LoadingButton>
                    </Grid>
                </FormProvider>
            </CardContent>
        </Card>
    );
};
